import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getCandidateJobs } from '../../Services/CandidateJobs';
import { useEffect, useState } from 'react';
import { CandidateJobs } from './CandidateJobs';
import { formatDates } from '../../Utils';
import { NoJobsMessage } from './styles';
import { Loader } from '../../Components/Loader/Loader';
import { useTranslation } from 'react-i18next';

interface CandidateJobListProps {
  allExpanded: boolean;
  setAllExpanded: (value: boolean) => void;
}

export const CandidateJobList = ({
  allExpanded,
  setAllExpanded,
}: CandidateJobListProps) => {
  const { candidateId } = useParams();
  const [jobs, setJobs] = useState<CandidateJob[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const { data: jobsData } = useQuery({
    queryKey: ['getCandidateJobs', candidateId],
    queryFn: () => getCandidateJobs({ page: 1, count: 50, candidateId: candidateId }),
    enabled: !!candidateId,
  });

  useEffect(() => {
    if (jobsData?.items) {
      const formattedJobs = jobsData.items.map((item: CandidateJob) => formatDates(item));
      setJobs(formattedJobs);
      setAllExpanded(formattedJobs.length === 1);
    } else {
      setJobs([]);
    }
    setLoading(false);
  }, [jobsData, setAllExpanded]);

  const updateCandidateJob = (updatedJob: CandidateJob) => {
    setJobs((prevJobs) => prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job)));
  };

  let content;

  if (loading) {
    content = <Loader isLoading={loading} />;
  } else if (jobs.length > 0) {
    content = jobs.map((job, index) => (
      <CandidateJobs
        key={job.id ?? `job-${index}`}
        candidateJob={job}
        updateCandidateJob={updateCandidateJob}
        allExpanded={allExpanded}
        hasOnlyOneJob={jobs.length === 1}
      />
    ));
  } else {
    content = <NoJobsMessage>{t('jobs.assign.no_jobs_message')}</NoJobsMessage>;
  }

  return <>{content}</>;
};